import { Award, Play, Quote } from "lucide-react";
import { Link } from "react-router-dom";
import type { Testimonial } from "../data/testimonials";

interface TestimonialCardProps {
  testimonial: Testimonial;
  onPlayVideo: () => void;
}

/** Initials-avatar badge, not a photo — see testimonials.ts's header comment for why. */
export default function TestimonialCard({ testimonial, onPlayVideo }: TestimonialCardProps) {
  return (
    <div className="flex h-full flex-col rounded-2xl bg-white p-6 shadow-sm ring-1 ring-ocean-900/5">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal-400/20 font-display text-sm font-bold text-teal-600">
          {testimonial.initials}
        </div>
        <div className="min-w-0">
          <p className="truncate font-display text-sm font-semibold text-ocean-950">{testimonial.name}</p>
          <p className="truncate text-xs text-ocean-800/50">
            {testimonial.role ? `${testimonial.role} · ` : ""}
            {testimonial.city}
          </p>
        </div>
      </div>

      {testimonial.stat && (
        <div className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-teal-400/15 px-3 py-1 text-xs font-semibold text-teal-600">
          <Award className="h-3.5 w-3.5" />
          {testimonial.stat}
        </div>
      )}

      <Quote className="mt-4 h-5 w-5 text-teal-400" />
      <p className="mt-2 flex-1 text-sm leading-relaxed text-ocean-800/80">{testimonial.quote}</p>

      {testimonial.media && (
        <button
          type="button"
          onClick={onPlayVideo}
          className="mt-4 inline-flex w-fit items-center gap-1.5 text-xs font-semibold text-teal-600 hover:text-teal-700"
        >
          <Play className="h-3.5 w-3.5" fill="currentColor" />
          Watch video testimonial
        </button>
      )}

      {testimonial.linkTo && (
        <Link
          to={testimonial.linkTo.href}
          className="mt-3 text-xs font-semibold text-teal-600 hover:text-teal-700"
        >
          {testimonial.linkTo.label} →
        </Link>
      )}
    </div>
  );
}
